import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  notificationsApi,
  type NotificationDto,
} from '../shared/api/notifications';

const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const BellIcon = () => (
  <svg {...iconProps}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const EmptyIllustration = () => (
  <svg
    width={120}
    height={80}
    viewBox="0 0 120 80"
    fill="none"
    aria-hidden="true"
    className="opacity-70"
  >
    <defs>
      <linearGradient id="nb-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.15" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
      </linearGradient>
    </defs>
    <rect x="6" y="14" width="108" height="56" rx="10" fill="url(#nb-bg)" />
    <path
      d="M30 32h60M30 44h44M30 56h28"
      stroke="currentColor"
      strokeOpacity="0.35"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="100" cy="22" r="6" fill="currentColor" fillOpacity="0.2" />
    <path
      d="M97 22l2 2 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function actorInitials(actor: NotificationDto['actor']): string {
  if (!actor) return '·';
  const name = actor.displayName?.trim();
  if (name) {
    const parts = name.split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
  }
  return actor.email?.[0]?.toUpperCase() ?? '?';
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diff = Date.now() - then;
  const sec = Math.max(1, Math.round(diff / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface NotificationItemProps {
  item: NotificationDto;
  onSelect: (item: NotificationDto) => void;
}

function NotificationItem({ item, onSelect }: NotificationItemProps) {
  const isUnread = !item.readAt;
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="group flex w-full items-start gap-3 px-3 py-2.5 text-left
                 transition-colors hover:bg-slate-100
                 dark:hover:bg-white/5
                 focus:outline-none focus-visible:bg-slate-100
                 dark:focus-visible:bg-white/5"
    >
      <span
        className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full
                   bg-gradient-brand text-[11px] font-semibold text-white shadow-glow-brand"
        aria-hidden="true"
      >
        <span className="absolute inset-0 ring-holo opacity-25 mix-blend-screen rounded-full" />
        <span className="relative z-10">{actorInitials(item.actor)}</span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={`truncate text-sm ${
              isUnread
                ? 'font-semibold text-slate-900 dark:text-slate-50'
                : 'font-medium text-slate-700 dark:text-slate-200'
            }`}
          >
            {item.title}
          </span>
          {isUnread && (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-accent-400"
              aria-label="Unread"
            />
          )}
        </span>
        {item.body && (
          <span
            className="mt-0.5 line-clamp-2 block text-xs text-slate-500 dark:text-slate-400"
          >
            {item.body}
          </span>
        )}
        <span className="mt-1 block text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
          {relativeTime(item.createdAt)}
        </span>
      </span>
    </button>
  );
}

export function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / escape. Because the dropdown is rendered via
  // a portal (so it can sit above modals), it lives outside popoverRef in
  // the DOM. We detect "outside" by also accepting clicks inside any
  // [data-notification-popover] element as inside.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const insideButton =
        popoverRef.current && popoverRef.current.contains(target as Node);
      const insidePopover = target?.closest?.('[data-notification-popover]');
      if (!insideButton && !insidePopover) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const countQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationsApi.list({ limit: 20 }),
    enabled: open, // only fetch the panel contents when opened
    staleTime: 5_000,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onMutate: async (id) => {
      // Optimistic: flip readAt locally so the dot disappears immediately.
      await qc.cancelQueries({ queryKey: ['notifications', 'list'] });
      const previousList = qc.getQueryData<{
        items: NotificationDto[];
        nextCursor: string | null;
      }>(['notifications', 'list']);
      const previousCount = qc.getQueryData<{ count: number }>([
        'notifications',
        'unread-count',
      ]);
      qc.setQueryData<{ items: NotificationDto[]; nextCursor: string | null }>(
        ['notifications', 'list'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((n) =>
              n.id === id && !n.readAt
                ? { ...n, readAt: new Date().toISOString() }
                : n,
            ),
          };
        },
      );
      qc.setQueryData<{ count: number }>(
        ['notifications', 'unread-count'],
        (old) => (old ? { count: Math.max(0, old.count - 1) } : old),
      );
      return { previousList, previousCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previousList) {
        qc.setQueryData(['notifications', 'list'], ctx.previousList);
      }
      if (ctx?.previousCount) {
        qc.setQueryData(['notifications', 'unread-count'], ctx.previousCount);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.setQueryData(['notifications', 'unread-count'], { count: 0 });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const items = useMemo(() => listQuery.data?.items ?? [], [listQuery.data]);
  const unread = countQuery.data?.count ?? 0;

  function handleSelect(item: NotificationDto) {
    if (!item.readAt) markReadMutation.mutate(item.id);
    if (item.linkPath) {
      navigate(item.linkPath);
    }
    setOpen(false);
  }

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        className="btn-icon relative"
        aria-label={
          unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'
        }
        title={
          unread > 0 ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <BellIcon />
        {unread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center
                       rounded-full bg-accent-500 px-1 text-[10px] font-semibold leading-none
                       text-white shadow-soft ring-2 ring-white dark:ring-slate-950"
            aria-hidden="true"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          role="dialog"
          aria-label="Notifications"
          // Portal-rendered into document.body so we escape the topbar's
          // backdrop-filter stacking context. Positioned via `fixed` so we
          // sit on top of every modal/drawer regardless of their z-index.
          // We also wire the same outside-click ref behaviour by stamping
          // a data attribute the popoverRef test can recognise.
          data-notification-popover
          className="fixed right-4 top-14 z-[1000] flex w-[22rem] max-w-[calc(100vw-1rem)] flex-col
                     overflow-hidden rounded-lg border border-slate-200
                     bg-white dark:bg-slate-900 shadow-soft-lg
                     dark:border-slate-800
                     animate-slide-up"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Notifications
            </div>
            {unread > 0 && (
              <span className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {unread} unread
              </span>
            )}
          </div>

          <div className="max-h-[24rem] flex-1 overflow-y-auto">
            {listQuery.isLoading && (
              <div className="px-3 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                Loading…
              </div>
            )}
            {listQuery.isError && (
              <div className="px-3 py-6 text-center text-sm text-rose-500">
                Couldn&apos;t load notifications.
              </div>
            )}
            {!listQuery.isLoading &&
              !listQuery.isError &&
              items.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-2 px-3 py-8 text-center text-slate-500 dark:text-slate-400">
                  <EmptyIllustration />
                  <span className="text-sm">You&apos;re all caught up.</span>
                </div>
              )}
            {items.length > 0 && (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((it) => (
                  <li key={it.id}>
                    <NotificationItem item={it} onSelect={handleSelect} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t border-slate-100 p-2 dark:border-slate-800">
              <button
                type="button"
                disabled={unread === 0 || markAllReadMutation.isPending}
                onClick={() => markAllReadMutation.mutate()}
                className="w-full rounded-md px-3 py-2 text-sm font-medium
                           text-slate-700 transition-colors
                           hover:bg-slate-100
                           disabled:cursor-not-allowed disabled:opacity-50
                           dark:text-slate-200 dark:hover:bg-white/5"
              >
                {markAllReadMutation.isPending
                  ? 'Marking as read…'
                  : 'Mark all as read'}
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
